import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Box, Grid, Paper, Typography } from '@material-ui/core';
import { Sticky, StickyContainer } from 'react-sticky';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import EditIcon from '@material-ui/icons/Edit';
import { useJobForm } from '../../hooks/useJobForm';
import JobForm from '../JobForm/JobForm';
import JobView from '../JobViewPublic/components/JobView';
import Button from '@material-ui/core/Button';
import PropTypes from 'prop-types';
import { APP_PATH_DASHBOARD, APP_PATH_PRICING } from '../../config';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  createJobThunk,
  jobCreatingSelector,
  updateJobThunk
} from '../../redux/features/job/jobsSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 0,
    padding: theme.spacing(4)
  },
  headerSectionView: {
    marginBottom: 10,
    padding: 20
  },
  stepperBtn: {
    textTransform: 'none',
    fontSize: '20px',
    marginRight: theme.spacing(3),
    marginTop: theme.spacing(3)
  },
  titleTopSection: {
    textAlign: 'center',
    paddingTop: 14,
    paddingBottom: 4
  },
  selectedPlanBox: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.success.main}08 100%)`,
    borderRadius: theme.spacing(1),
    border: `2px solid ${theme.palette.primary.main}30`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  planInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  planName: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary
  },
  changePlanBtn: {
    textTransform: 'none',
    fontSize: '0.875rem'
  }
}));

const EditorJobView = ({ previewTitle }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    formValues,
    handleChange,
    handleChangeCustomFieldName,
    hasError,
    formErrors,
    isValidForm,
    isEditMode
  } = useJobForm();
  const history = useHistory();
  const isCreating = useSelector(jobCreatingSelector);

  // Read selected plan from sessionStorage
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    const storedPlan = sessionStorage.getItem('selectedJobPlan');
    if (storedPlan) {
      try {
        setSelectedPlan(JSON.parse(storedPlan));
      } catch (e) {
        console.error('Failed to parse selected plan:', e);
      }
    }
  }, []);

  const handleChangePlan = () => {
    history.push(APP_PATH_PRICING);
  };

  const handleSave = () => {
    if (isEditMode) {
      // Update existing job - just dispatch, don't await
      console.log('📝 [EditorJobView] Updating job...');
      dispatch(updateJobThunk({ job: formValues }));
      // Go back after dispatch (navigation will happen before API completes)
      history.goBack();
    } else {
      // Create new job and return to dashboard on success
      console.log('📝 [EditorJobView] Creating job...');
      dispatch(createJobThunk({ job: formValues }));
      history.push(APP_PATH_DASHBOARD);
    }
  };
  return (
    <Paper className={classes.root}>
      <StickyContainer>
        <Grid container spacing={3} direction="row" justifyContent="center">
          <Grid item lg={6} md={6} xl={6} xs={12}>
            <Typography
              className={classes.titleTopSection}
              variant="h3"
              align="center">
              {isEditMode ? 'EDIT JOB' : 'JOB DETAILS'}
            </Typography>
            {selectedPlan && !isEditMode && (
              <Box className={classes.selectedPlanBox}>
                <Box className={classes.planInfo}>
                  <CheckCircleIcon style={{ color: '#4caf50' }} />
                  <Typography className={classes.planName}>
                    {selectedPlan.planName} Selected
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleChangePlan}
                  className={classes.changePlanBtn}>
                  Change Plan
                </Button>
              </Box>
            )}
            <JobForm
              formErrors={formErrors}
              formValues={formValues}
              handleChangeCustomFieldName={handleChangeCustomFieldName}
              handleChange={handleChange}
              hasError={hasError}
            />
          </Grid>
          <Grid item lg={6} md={6} xl={6} xs={12}>
            <Sticky>
              {({ style }) => (
                <div style={style}>
                  <Paper className={classes.headerSectionView} elevation={0}>
                    <Typography variant="h3" align="center">
                      {previewTitle}
                    </Typography>
                  </Paper>
                  <JobView isPreview job={formValues} />
                </div>
              )}
            </Sticky>
          </Grid>
        </Grid>
      </StickyContainer>
      <Button
        data-test="next-step-button-assistant"
        className={classes.stepperBtn}
        color="primary"
        disabled={isValidForm || isCreating}
        onClick={handleSave}
        variant="contained">
        {isCreating ? 'Saving...' : 'Save'}
      </Button>
      {isEditMode && (
        <Button
          data-test="cancel-button-assistant"
          className={classes.stepperBtn}
          color="default"
          onClick={() => history.goBack()}
          variant="contained">
          Cancel
        </Button>
      )}
    </Paper>
  );
};

EditorJobView.defaultProps = {
  previewTitle: 'JOB PREVIEW'
};

EditorJobView.propTypes = {
  previewTitle: PropTypes.string
};

export default EditorJobView;
